import { Box, Button } from '@palmetto/palmetto-components';
import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { Flag, QueueStatus, QueueType } from 'types';

import { ReviewPackageActions } from '../../types/ReviewPackage';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { CompleteQueueReview } from '../Queues/CompleteQueueReview/CompleteQueueReview';

interface ActionListFooterRHFProps {
  isLoading: boolean;
  canSubmitPackage: boolean;
  canSubmitReview: boolean;
  disableApproval?: boolean;
  disableAllApprovals?: boolean;
  disabled?: boolean;
  onSave: ({ accountId, data }: { accountId: string; data: Record<string, any> }) => Promise<void>;
  actionButtonText?: string;
  conditionallyApprovedFlags?: Flag[];
  subQueueInfo?: {
    type: QueueType | undefined;
    status: QueueStatus | undefined;
  };
}
export const ActionListFooterRHF = ({
  isLoading,
  canSubmitPackage,
  canSubmitReview,
  disableApproval,
  disableAllApprovals,
  onSave,
  disabled,
  actionButtonText,
  conditionallyApprovedFlags,
  subQueueInfo,
}: ActionListFooterRHFProps) => {
  const { id = '', action = 'review' } = useParams<{ id: string; action: ReviewPackageActions }>();
  const {
    getValues,
    formState: { isSubmitting, errors, touchedFields },
  } = useFormContext<any>();

  const allowsAction = useIsOrgReadOnly();

  const pureSave = useCallback(async () => {
    await onSave({ accountId: id, data: getValues() });
  }, [onSave, id, getValues]);

  const formErrors = Object.keys(errors).reduce<Record<string, any>>((acc, key) => {
    if (touchedFields[key]) {
      acc[key] = (errors as Record<string, any>)[key];
    }
    return acc;
  }, {});

  return (
    <Box direction={'column'} childGap="sm" padding={'0'} alignItems={'flex-end'}>
      {action === ReviewPackageActions.review ? (
        <CompleteQueueReview
          canSubmitReview={canSubmitReview}
          disableApproval={disableApproval}
          disableAllApprovals={disableAllApprovals}
          handleFormSave={pureSave}
          conditionallyApprovedFlags={conditionallyApprovedFlags}
          subQueueInfo={{ type: subQueueInfo?.type, status: subQueueInfo?.status }}
        />
      ) : (
        <Button
          size="md"
          variant="primary"
          type="submit"
          isLoading={isSubmitting || isLoading}
          disabled={
            !canSubmitPackage || !!Object.keys(formErrors).length || disabled || isSubmitting || !allowsAction
          }
        >
          {actionButtonText}
        </Button>
      )}
    </Box>
  );
};
